import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { HelpCircle, X } from "lucide-react";
import { createPortal } from "react-dom";
import { useI18n } from "@/lib/i18n";

const STORAGE_ENABLED = "tc_assistant_enabled_v1";
const STORAGE_DISMISSED = "tc_assistant_seen_v1";
const AssistantCtx = createContext(null);

// Global provider. Persists enable-flag + set of already-seen tip IDs.
export function AssistantProvider({ children }) {
  const [enabled, setEnabled] = useState(() => {
    const v = localStorage.getItem(STORAGE_ENABLED);
    return v === null ? true : v === "true"; // default ON
  });
  const [seen, setSeen] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(STORAGE_DISMISSED) || "[]")); }
    catch { return new Set(); }
  });
  const [activeTip, setActiveTip] = useState(null); // {id, text, anchorRect}
  const [pageTips, setPageTips] = useState([]); // list of tips currently mounted on this page
  const queueRef = useRef([]);

  const persistSeen = (s) => localStorage.setItem(STORAGE_DISMISSED, JSON.stringify(Array.from(s)));
  const persistEnabled = (v) => localStorage.setItem(STORAGE_ENABLED, String(v));

  const toggleEnabled = () => setEnabled((e) => { persistEnabled(!e); return !e; });

  const markSeen = (id) => setSeen((prev) => { const s = new Set(prev); s.add(id); persistSeen(s); return s; });

  const dequeueNext = useCallback(() => {
    setActiveTip((cur) => cur); // ensure single-flight guard
    if (queueRef.current.length === 0) { setActiveTip(null); return; }
    const next = queueRef.current.shift();
    setActiveTip(next);
    setTimeout(() => {
      setActiveTip((t) => (t && t.id === next.id ? null : t));
      dequeueNext();
    }, 6000);
  }, []);

  const registerTip = useCallback((tip) => {
    setPageTips((p) => (p.find((t) => t.id === tip.id) ? p : [...p, tip]));
    // Auto-enqueue if enabled and not seen yet
    if (enabled && !seen.has(tip.id)) {
      queueRef.current.push(tip);
      markSeen(tip.id);
      if (!activeTip) dequeueNext();
    }
  }, [enabled, seen, activeTip, dequeueNext]);

  const unregisterTip = useCallback((id) => {
    setPageTips((p) => p.filter((t) => t.id !== id));
  }, []);

  const repeatPage = useCallback(() => {
    queueRef.current = [...pageTips];
    dequeueNext();
  }, [pageTips, dequeueNext]);

  const showTip = useCallback((tip) => { queueRef.current.push(tip); if (!activeTip) dequeueNext(); }, [activeTip, dequeueNext]);

  // Reset page-scoped tips when navigating (basic heuristic: history)
  useEffect(() => { setPageTips([]); queueRef.current = []; setActiveTip(null); }, [typeof window !== "undefined" ? window.location.pathname : ""]);

  return (
    <AssistantCtx.Provider value={{
      enabled, toggleEnabled, registerTip, unregisterTip, showTip,
      repeatPage, activeTip, setActiveTip, pageTipsCount: pageTips.length,
    }}>
      {children}
      {activeTip && <TipBubble tip={activeTip} onClose={() => setActiveTip(null)} />}
      {pageTips.length > 0 && enabled && <RepeatTab pageTips={pageTips} />}
    </AssistantCtx.Provider>
  );
}

export function useAssistant() {
  const ctx = useContext(AssistantCtx);
  if (!ctx) throw new Error("useAssistant must be used inside AssistantProvider");
  return ctx;
}

// Convenience hook: register a list of page-scoped tips.
// The `tips` array can contain either resolved `{id, text}` items OR
// `{id, textKey}` items — the second form is preferred because it re-runs
// registration whenever the app language changes, so the tip bubble is
// always shown in the currently selected UI language.
export function useAssistantPageTips(tips) {
  const { registerTip, unregisterTip } = useAssistant();
  const { t, lang } = useI18n();
  // Snapshot of tip identities we depend on. We deliberately re-key on
  // `lang` so localized text is refreshed on language change without
  // triggering an infinite render loop.
  const tipIds = (tips || []).map((tp) => tp?.id || "").join("|");
  useEffect(() => {
    if (!tips || tips.length === 0) return;
    const cleanupIds = [];
    tips.forEach((tip, i) => {
      if (!tip || !tip.id) return;
      const text = tip.textKey ? t(tip.textKey) : tip.text;
      if (!text) return;
      setTimeout(() => registerTip({ id: tip.id, text, anchor: null }), 200 + i * 250);
      cleanupIds.push(tip.id);
    });
    return () => cleanupIds.forEach((id) => unregisterTip(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipIds, lang]);
}

// Hook used by pages: register a tip anchored to a ref element by id.
export function useAssistantTip(id, text, anchorRef) {
  const { registerTip, unregisterTip, enabled } = useAssistant();
  useEffect(() => {
    if (!id || !text) return;
    const tick = setTimeout(() => {
      const el = anchorRef?.current;
      if (!el) { registerTip({ id, text, anchor: null }); return; }
      const rect = el.getBoundingClientRect();
      registerTip({ id, text, anchor: { top: rect.top, left: rect.left, width: rect.width, height: rect.height, el } });
    }, 400);
    return () => { clearTimeout(tick); unregisterTip(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, text, enabled]);
}

// Tip bubble — positioned near anchor if provided, else bottom-right.
function TipBubble({ tip, onClose }) {
  const { t } = useI18n();
  const boxRef = useRef(null);
  useEffect(() => {
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [onClose]);
  const style = {};
  if (tip.anchor) {
    // Anchored to a specific control — position just below/right of it.
    const top = Math.min(window.innerHeight - 160, Math.max(70, tip.anchor.top + tip.anchor.height + 8));
    const left = Math.min(window.innerWidth - 300, Math.max(12, tip.anchor.left));
    Object.assign(style, { top: `${top}px`, left: `${left}px` });
  } else {
    // Floating tip — anchor to bottom-LEFT (not right) to keep the map's
    // right-side controls (GPS, filters, FAB, publish CTA) reachable on
    // mobile. Respects iOS safe-area inset.
    Object.assign(style, { bottom: "calc(env(safe-area-inset-bottom, 0px) + 68px)", left: "12px" });
  }
  return createPortal(
    <div
      ref={boxRef}
      data-testid={`assistant-tip-${tip.id}`}
      className="fixed z-[9999] max-w-[280px] rounded-2xl bg-emerald-900 text-white shadow-2xl px-4 py-3 border border-emerald-700 animate-in fade-in slide-in-from-bottom-2"
      style={style}
      role="dialog"
      aria-label={t("assistant_label")}
    >
      <div className="flex items-start gap-2">
        <HelpCircle size={16} className="mt-0.5 shrink-0 opacity-80" />
        <div className="text-[13px] leading-snug flex-1">{tip.text}</div>
        <button onClick={onClose} className="shrink-0 opacity-70 hover:opacity-100" aria-label={t("close")} data-testid={`assistant-close-${tip.id}`}>
          <X size={14} />
        </button>
      </div>
    </div>,
    document.body,
  );
}

// Small floating "?" button that opens a carousel of the page's tips.
function RepeatTab({ pageTips }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const dialogRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (dialogRef.current && !dialogRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);
  useEffect(() => { if (open) setIdx(0); }, [open]);
  const total = pageTips.length;
  if (total === 0) return null;
  const cur = pageTips[Math.min(idx, total - 1)];
  return createPortal(
    <>
      <button
        type="button"
        data-testid="assistant-repeat-btn"
        onClick={() => setOpen((o) => !o)}
        aria-label={t("assistant_open_tips")}
        title={t("page_tips_title")}
        className="fixed z-[9998] w-9 h-9 rounded-full bg-white/95 hover:bg-emerald-50 text-emerald-700 shadow-lg border border-emerald-300 text-base font-bold inline-flex items-center justify-center backdrop-blur-sm"
        style={{
          // Lotto 4 #12 — anchored to the right edge, vertically centred, so it
          // never overlaps map controls (filters top-left, tile-mode top-right,
          // zoom bottom-left, FAB Pubblica bottom-centre/right) nor form fields.
          // Safe area padding keeps it above iOS home indicator.
          top: "50%",
          right: "8px",
          transform: "translateY(-50%)",
        }}
      >
        ?
      </button>
      {open && cur && (
        <div
          ref={dialogRef}
          data-testid="assistant-carousel"
          role="dialog"
          aria-label={t("page_tips_title")}
          className="fixed z-[9999] w-[260px] max-w-[calc(100vw-32px)] rounded-2xl bg-white shadow-2xl border border-emerald-200 overflow-hidden"
          style={{
            // Position the tips card to the LEFT of the "?" button, vertically
            // aligned. On narrow screens it stays inside the viewport thanks to
            // max-width and the 12px right offset.
            top: "50%",
            right: "56px",
            transform: "translateY(-50%)",
          }}
        >
          <div className="px-4 py-3">
            <div className="text-[13px] leading-snug text-slate-800" data-testid="assistant-carousel-text">{cur.text}</div>
          </div>
          <div className="flex items-center justify-between px-3 py-2 bg-emerald-50 border-t border-emerald-100">
            <button
              type="button"
              onClick={() => setIdx((i) => (i - 1 + total) % total)}
              disabled={total < 2}
              data-testid="assistant-carousel-prev"
              className="w-7 h-7 rounded-full bg-white border border-emerald-300 text-emerald-800 text-sm disabled:opacity-40 hover:bg-emerald-100"
              aria-label={t("previous_label")}
            >‹</button>
            <div className="text-[11px] text-emerald-800 font-semibold" data-testid="assistant-carousel-index">{Math.min(idx, total - 1) + 1}/{total}</div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setIdx((i) => (i + 1) % total)}
                disabled={total < 2}
                data-testid="assistant-carousel-next"
                className="w-7 h-7 rounded-full bg-white border border-emerald-300 text-emerald-800 text-sm disabled:opacity-40 hover:bg-emerald-100"
                aria-label={t("next_label")}
              >›</button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="assistant-carousel-close"
                className="w-7 h-7 rounded-full bg-white border border-slate-300 text-slate-700 text-sm hover:bg-slate-100"
                aria-label={t("close")}
              >×</button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body,
  );
}

// Header toggle chip
export function AssistantToggle() {
  const { enabled, toggleEnabled } = useAssistant();
  const { t } = useI18n();
  return (
    <button
      type="button"
      onClick={toggleEnabled}
      data-testid="assistant-toggle"
      className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[11px] font-semibold border ${enabled ? "bg-emerald-700 text-white border-emerald-700 hover:bg-emerald-800" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"}`}
      title={enabled ? t("assistant_status_on") : t("assistant_status_off")}
    >
      <HelpCircle size={13} />
      {t("assistant_label")}: {enabled ? t("on") : t("off")}
    </button>
  );
}
